import c from "classnames";

import { Clickable } from "@design-system/Clickable/Clickable";
import { Skeleton } from "@design-system/Skeleton/Skeleton";
import { Text } from "@design-system/Text/Text";

import classes from "./DashboardFigure.module.css";

type Props = {
  label: string;
  value: string;
  /** Window or scope, when the figure is not part of the shared range. */
  detail?: string;
  isLoading?: boolean;
  onClick?: () => void;
  /**
   * Lead row at the top of a dashboard. A figure inside another widget
   * stays compact so it does not compete with that widget's title.
   */
  prominent?: boolean;
  /**
   * Label above the value. Use when the value is a phrase, such as
   * "127 days ago", that cannot sit beside the label in a narrow card.
   */
  stacked?: boolean;
};

export const DashboardFigure = ({
  label,
  value,
  detail,
  isLoading = false,
  onClick,
  prominent = false,
  stacked = false,
}: Props) => {
  return (
    <div
      className={c(
        classes.figure,
        prominent && classes.prominent,
        stacked && classes.stacked,
      )}
    >
      {isLoading ? (
        <Skeleton width={prominent ? 64 : 48} height={prominent ? 32 : 21} />
      ) : (
        <span className={classes.value}>{value}</span>
      )}
      <Text
        size={prominent ? 14 : 12}
        weight={prominent ? 600 : 400}
        color={prominent ? "textDark" : "textSecondary"}
      >
        {label}
      </Text>
      {detail ? (
        <span className={classes.detail}>
          <Text size={11} color="textSecondary">
            {detail}
          </Text>
        </span>
      ) : null}
      {onClick ? (
        <Clickable
          onClick={onClick}
          aria-label={label}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
          }}
        />
      ) : null}
    </div>
  );
};
